/**
 * dbtfy-send-for-visualization.js
 *
 * Powers the "Изпрати за визуализация" button.
 *
 * What it does, step by step:
 *  1. On click, finds the nearest Shopify product form on the page
 *     (the same form Aris Product Options writes its fields into).
 *  2. Pulls out the fields it cares about: an uploaded photo URL,
 *     a description/text field, email, and phone — matched loosely by
 *     common name patterns so this keeps working if Aris's exact field
 *     names change slightly. Adjust FIELD_MATCHERS below if needed.
 *  3. Validates that the required fields are present.
 *  4. POSTs everything (+ product id, variant id, shop domain) as JSON
 *     to the backend URL from Theme Settings.
 *  5. On success, adds the item to cart directly via Shopify's
 *     /cart/add.js, serializing the same form Aris wrote its line item
 *     properties into — then waits for that to finish before moving on.
 *  6. Redirects to /cart, where a banner (see
 *     sections/dbtfy-visualization-status.liquid) tells the customer
 *     their visualization is being worked on. The customer is never
 *     blocked from checking out immediately — approval happens entirely
 *     on the backend/fulfillment side, after payment if they choose to
 *     pay right away.
 *
 * This script does NOT create Shopify Draft Orders itself — that must
 * happen on the backend, which receives this POST request. See the
 * accompanying architecture notes for why that split exists.
 */
(function () {
  "use strict";

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  // Loose matchers for Aris-style field names/labels. Update these if your
  // Aris field setup uses different names — easiest fix is to add the exact
  // The real Aris (Avis Product Options) field names on this store, as
  // they appear in the cart form. Aris prefixes line-item properties with
  // "properties[...]". Photos come as multiple numbered fields
  // (Снимки-1, Снимки-2, ...), each holding a hosted image URL.
  var FIELD_MATCHERS = {
    // Description: "Име, цвят на очи и коса на всеки герой." (note the
    // trailing space Aris includes). Matched loosely to survive small
    // label edits.
    // Description on the current products: "Име, цвят на очи и коса на
    // всеки герой." Extra patterns are there so a future product that
    // words its field slightly differently still works. If none of these
    // match, findDescriptionField() falls back to the product form's
    // free-text box — see there.
    description: [/цвят на очи/i, /описание/i, /име.*коса/i, /герой/i, /персонаж/i],
    email: [/^properties\[Имейл\]$/i, /имейл/i, /email/i, /e-mail/i],
    phone: [/^properties\[Телефон\]$/i, /телефон/i, /phone/i, /tel/i]
  };

  // What counts as "a photo field". Aris names these differently per
  // product — confirmed on this store (28 Jul 2026):
  //   book:    properties[Снимки-1], properties[Снимки-2]  (numbered)
  //   magnets: properties[Снимки]                          (no number!)
  // so the number must be optional. Also tolerates a singular "Снимка".
  // Shared by collectPhotoUrls and productHasVisualizationFields below,
  // so both always agree on what a photo field is.
  var PHOTO_FIELD_NAME_RE = /(^|\[)\s*Снимк/i;
  var URL_VALUE_RE = /^https?:\/\//i;

  // Collects ALL photo URLs the customer uploaded, whatever Aris named
  // the fields on this particular product.
  function collectPhotoUrls(form) {
    var urls = [];
    var inputs = form.querySelectorAll("input[name], textarea[name]");
    for (var i = 0; i < inputs.length; i++) {
      var name = inputs[i].name || "";
      var val = (inputs[i].value || "").trim();
      // Skip empties and the browser's "C:\fakepath\..." on file inputs.
      if (!val || val.indexOf("fakepath") !== -1) continue;

      if (PHOTO_FIELD_NAME_RE.test(name)) {
        if (urls.indexOf(val) === -1) urls.push(val);
      } else if (/^properties\[/i.test(name) && URL_VALUE_RE.test(val)) {
        // Safety net: an Aris property holding a hosted URL is almost
        // certainly an upload, even if the field is named something we
        // didn't anticipate on a future product.
        if (urls.indexOf(val) === -1) urls.push(val);
      }
    }
    return urls;
  }

  /**
   * Is THIS product actually a personalized one?
   *
   * The button's markup is rendered by a shared product snippet, so it
   * would otherwise appear on every product page — including plain,
   * non-personalized products (boxes, magnets, cards...), where "Изпрати
   * за визуализация" makes no sense and wrongly replaces "Добави в
   * кошницата".
   *
   * Rather than maintaining a manual list of product IDs (easy to forget
   * when adding a new product), we detect it from the page itself: Aris
   * only renders its fields on products that have the personalization
   * option set. We look for a photo-upload field AND an email field —
   * the two things this flow cannot work without, and the two that every
   * personalized product here has (book, magnets, and anything added
   * later that follows the same setup). Products without Aris — карти за
   * игра, живи книги — have neither, so they keep the theme's normal
   * "Добави в кошницата" button untouched.
   *
   * Deliberately NOT keyed on the description field: its wording varies
   * the most between products, so relying on it would silently break the
   * button on a future product that phrases it differently.
   *
   * Checked against the whole document rather than one form, because Aris
   * can render its visible widgets outside the add-to-cart form and mirror
   * the values into hidden inputs inside it.
   */
  function productHasVisualizationFields() {
    var fields = document.querySelectorAll(
      "input[name], textarea[name], select[name]"
    );

    var hasPhoto = false;
    var hasEmail = false;

    for (var i = 0; i < fields.length; i++) {
      var el = fields[i];
      var name = el.name || "";
      var haystack = name + " " + (el.id || "") + " " + (el.getAttribute("data-label") || "") + " " + (el.placeholder || "");

      if (!hasPhoto && PHOTO_FIELD_NAME_RE.test(name)) hasPhoto = true;
      if (!hasEmail && matchesAny(haystack, FIELD_MATCHERS.email)) hasEmail = true;

      if (hasPhoto && hasEmail) return true;
    }

    return false;
  }

  function matchField(form, matchers) {
    var candidates = form.querySelectorAll(
      "input[name], textarea[name], select[name]"
    );

    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      var haystack = (el.name || "") + " " + (el.id || "") + " " + (el.getAttribute("data-label") || "") + " " + (el.placeholder || "");
      for (var m = 0; m < matchers.length; m++) {
        if (matchers[m].test(haystack)) {
          return el;
        }
      }
    }
    return null;
  }

  /**
   * Finds the free-text "describe your characters" field.
   *
   * Normally matched by name via FIELD_MATCHERS.description. But if a
   * future product words that field differently ("Име и възраст на
   * детето", say), we'd otherwise fail with "попълнете описание" even
   * though the customer filled everything in. So as a fallback we take
   * the form's Aris textarea — on these products the long free-text box
   * IS the description; photo/email/phone are plain inputs, not textareas.
   */
  function findDescriptionField(form) {
    var matched = matchField(form, FIELD_MATCHERS.description);
    if (matched) return matched;

    var textareas = form.querySelectorAll("textarea[name]");
    for (var i = 0; i < textareas.length; i++) {
      var name = textareas[i].name || "";
      if (!/^properties\[/i.test(name)) continue;
      // Don't mistake email/phone for the description if they happen to
      // be rendered as textareas.
      if (matchesAny(name, FIELD_MATCHERS.email)) continue;
      if (matchesAny(name, FIELD_MATCHERS.phone)) continue;
      if (PHOTO_FIELD_NAME_RE.test(name)) continue;
      return textareas[i];
    }
    return null;
  }

  function matchesAny(haystack, matchers) {
    for (var i = 0; i < matchers.length; i++) {
      if (matchers[i].test(haystack)) return true;
    }
    return false;
  }

  function getFieldValue(el) {
    if (!el) return "";
    if (el.type === "checkbox" || el.type === "radio") {
      return el.checked ? el.value : "";
    }
    return (el.value || "").trim();
  }

  function findProductForm(root) {
    // Aris and the theme's own add-to-cart both live inside
    // form[action*="/cart/add"] (see theme.ajaxCart settings.formSelector).
    return (
      root.closest("[data-product-form]") ||
      document.querySelector("form[action*='/cart/add']")
    );
  }

  function addToCartDirectly(form) {
    // Serialize the actual product form (id, quantity, and crucially all
    // of Aris's properties[...] fields) so the line item properties the
    // customer just filled in end up on the cart line, same as if they'd
    // clicked the native button themselves.
    var formData = new FormData(form);

    return fetch("/cart/add.js", {
      method: "POST",
      headers: { Accept: "application/json" },
      body: formData
    }).then(function (response) {
      if (!response.ok) {
        return response.json().then(function (body) {
          throw new Error((body && body.description) || "cart_add_failed");
        });
      }
      return response.json();
    });
  }

  function setLoading(container, isLoading) {
    var button = container.querySelector("[data-dbtfy-send-for-viz-button]");
    var spinner = container.querySelector(".dbtfy-send-for-viz__spinner");
    if (!button) return;
    button.disabled = isLoading;
    button.classList.toggle("is-loading", isLoading);
    if (spinner) spinner.hidden = !isLoading;
  }

  function showInlineError(container, message) {
    var el = container.querySelector("[data-dbtfy-send-for-viz-inline-error]");
    if (!el) return;
    if (!message) {
      el.hidden = true;
      el.textContent = "";
      return;
    }
    el.hidden = false;
    el.textContent = message;
  }

  function collectPayload(form, productId) {
    var photoUrls = collectPhotoUrls(form);
    var descriptionField = findDescriptionField(form);
    var emailField = matchField(form, FIELD_MATCHERS.email);
    var phoneField = matchField(form, FIELD_MATCHERS.phone);

    var variantInput = form.querySelector("[name='id']");

    return {
      shop: window.Shopify && window.Shopify.shop ? window.Shopify.shop : window.location.hostname,
      product_id: productId,
      variant_id: variantInput ? variantInput.value : null,
      // First photo URL goes in photo_url (backend's main field); all of
      // them go in photo_urls so the backend can attach every photo.
      photo_url: photoUrls.length ? photoUrls[0] : "",
      photo_urls: photoUrls,
      description: getFieldValue(descriptionField),
      email: getFieldValue(emailField),
      phone: getFieldValue(phoneField),
      page_url: window.location.href
    };
  }

  // Basic but real format checks — catches "Тест" in the email field and
  // "123456" in the phone field, which the old check let straight through
  // (it only verified the fields were non-empty, not that they looked like
  // a real email/phone). Bulgarian mobile numbers are matched loosely
  // enough to allow +359/00359/0 prefixes and spaces/dashes, while still
  // rejecting obvious junk like "123456".
  // The phone input sits next to a separate +359 country-code dropdown
  // (rendered by the Aris app), so el.value here is normally just the
  // local number the customer typed — e.g. "888888888". But older or
  // less tech-savvy customers sometimes type the leading 0 anyway, or
  // even the full +359/00359 prefix despite the dropdown already showing
  // it, or just leave off a digit. Rather than reject those, we accept
  // a generous range (8 to 10 digits after stripping any prefix) so the
  // check catches real junk like "123456" without tripping up someone
  // who reasonably guessed at the format.
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function isValidEmail(value) {
    return EMAIL_RE.test(value.trim());
  }

  function isValidPhone(value) {
    var digitsOnly = value.replace(/[\s()-]/g, "");
    // Strip an optional country-code prefix the customer may have typed
    // anyway (+359, 00359, or a leading 0), then check what's left is
    // 8-10 digits — generous enough for typos/extra-leading-zero cases
    // while still catching obvious junk like "123456".
    digitsOnly = digitsOnly.replace(/^(\+359|00359|359|0)/, "");
    return /^[0-9]{8,10}$/.test(digitsOnly);
  }

  function validatePayload(payload) {
    if (!payload.photo_url || !payload.description || !payload.email || !payload.phone) {
      return { ok: false, reason: "missing_fields" };
    }
    if (!isValidEmail(payload.email)) {
      return { ok: false, reason: "invalid_email" };
    }
    if (!isValidPhone(payload.phone)) {
      return { ok: false, reason: "invalid_phone" };
    }
    return { ok: true };
  }

  var SESSION_STORAGE_KEY = "dbtfy_last_visualization_submission";
  var STATUS_FLAG_KEY = "dbtfy_visualization_in_progress";

  function rememberSubmission(payload) {
    try {
      sessionStorage.setItem(
        SESSION_STORAGE_KEY,
        JSON.stringify({
          email: payload.email,
          phone: payload.phone,
          description: payload.description,
          photo_url: payload.photo_url
        })
      );
      // Tells the cart page to show the "working on your visualization"
      // banner. Cleared automatically the next time someone lands on the
      // cart with nothing pending (see dbtfy-visualization-status.js).
      sessionStorage.setItem(STATUS_FLAG_KEY, "1");
    } catch (e) {
      // sessionStorage can throw in some privacy modes — the redirect to
      // /cart still happens, the customer just won't see the banner.
    }
  }

  function sendForVisualization(container) {
    var apiUrl = container.getAttribute("data-api-url");
    var apiSecret = container.getAttribute("data-api-secret");
    var msgMissingApi = container.getAttribute("data-msg-missing-api");
    var msgGenericError = container.getAttribute("data-msg-generic-error");
    var msgMissingFields = container.getAttribute("data-msg-missing-fields");

    showInlineError(container, "");

    if (!apiUrl) {
      showInlineError(container, msgMissingApi);
      return;
    }

    var form = findProductForm(container);
    if (!form) {
      showInlineError(container, msgGenericError);
      return;
    }

    var productId = container.getAttribute("data-product-id");
    var payload = collectPayload(form, productId);

    var validation = validatePayload(payload);
    if (!validation.ok) {
      var msgInvalidEmail = container.getAttribute("data-msg-invalid-email");
      var msgInvalidPhone = container.getAttribute("data-msg-invalid-phone");
      if (validation.reason === "invalid_email") {
        showInlineError(container, msgInvalidEmail || msgMissingFields);
      } else if (validation.reason === "invalid_phone") {
        showInlineError(container, msgInvalidPhone || msgMissingFields);
      } else {
        showInlineError(container, msgMissingFields);
      }
      return;
    }

    setLoading(container, true);

    fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Visualization-Secret": apiSecret || ""
      },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        if (!response.ok) throw new Error("Request failed with status " + response.status);
        return response.json().catch(function () { return {}; });
      })
      .then(function () {
        rememberSubmission(payload);

        // Add to cart directly and wait for it to actually finish before
        // redirecting — clicking the theme's native button and racing a
        // separate fetch alongside it isn't reliable, since there's no
        // guarantee which finishes first.
        return addToCartDirectly(form);
      })
      .then(function () {
        window.location.href = "/cart";
      })
      .catch(function () {
        showInlineError(container, msgGenericError);
      })
      .finally(function () {
        setLoading(container, false);
      });
  }

  /**
   * Turns the button on, but ONLY on personalized products.
   *
   * Aris renders its fields asynchronously (the app's script runs after
   * ours), so the fields often aren't in the DOM yet when this first runs.
   * We therefore re-check for a short while: as soon as the fields appear
   * we activate; if they never appear (a plain, non-personalized product)
   * we simply stop, leaving the native "Добави в кошницата" button visible
   * and fully functional. Failing "closed" like this is deliberate — a
   * missing custom button is a much smaller problem than a normal product
   * that can't be bought.
   */
  function activateWhenPersonalized(vizContainer) {
    var CHECK_INTERVAL_MS = 250;
    var MAX_WAIT_MS = 8000; // plenty for Aris even on a slow connection
    var waited = 0;

    function activate() {
      vizContainer.hidden = false;
      document.body.classList.add("dbtfy-viz-active");

      // WARM-UP PING: Render's free tier spins the backend down after 15
      // minutes of inactivity, and the first request then takes 30-60s
      // (cold start) — long enough for a customer to give up. So the
      // moment someone opens a product page, we quietly ping the backend's
      // /health endpoint in the background. By the time they've picked a
      // photo and typed their details (usually 1-3 minutes), the server is
      // awake and the button responds in ~1-2 seconds. Fire-and-forget:
      // errors are ignored, this must never affect the page.
      var apiUrl = vizContainer.getAttribute("data-api-url") || "";
      if (apiUrl) {
        try {
          var origin = new URL(apiUrl).origin;
          fetch(origin + "/health", { method: "GET", mode: "cors" }).catch(function () {});
        } catch (e) {
          /* malformed URL — ignore */
        }
      }
    }

    if (productHasVisualizationFields()) {
      activate();
      return;
    }

    var timer = setInterval(function () {
      waited += CHECK_INTERVAL_MS;

      if (productHasVisualizationFields()) {
        clearInterval(timer);
        activate();
      } else if (waited >= MAX_WAIT_MS) {
        clearInterval(timer);
        // Not a personalized product — leave the page exactly as the theme
        // intended. The button stays hidden and the native one keeps working.
      }
    }, CHECK_INTERVAL_MS);
  }

  ready(function () {
    // The button markup exists on every product page (it lives in a shared
    // snippet), but it starts hidden and the native add-to-cart button
    // stays visible. Only once we've confirmed this product really is a
    // personalized one do we swap them — see productHasVisualizationFields.
    var vizContainer = document.querySelector("[data-dbtfy-send-for-viz]");
    if (vizContainer) {
      activateWhenPersonalized(vizContainer);
    }

    document.addEventListener("click", function (event) {
      var trigger = event.target.closest("[data-dbtfy-send-for-viz-button]");
      if (trigger) {
        var container = trigger.closest("[data-dbtfy-send-for-viz]");
        if (container) sendForVisualization(container);
      }
    });
  });
})();
